import { useEffect, useState, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Modal,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  Menu,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FilestoreBrowser from "./FilestoreBrowser";
import { FilestoreFile, parseFilestoreFile } from "../api/filestore";
import { Icon } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import {
  DrawingContext,
  DrawingMode,
  DrawingFeatureType,
} from "../contexts/DrawingContext";
import { useAddAsset, useUpdateAsset, useUpdateAssetById } from "../api/assets";
import { useAssetTypes } from "../api/assetTypes";
import { useProject } from "../api/project";
import {
  CONSTRUCTION_CATEGORIES,
  isBuildingAssetType,
  useConstructionTypes,
  useConstructionSettings,
} from "../api/constructionTypes";
import { useUnitSystem } from "../hooks/useUnitSystem";
import { feetToMeters, metersToFeet } from "../utils/units";
import {
  collectPropertyFields,
  collectFieldSamples,
  applyWidthConfig,
  hasGeometryOfType,
  LINE_GEOMETRY_TYPES,
  POINT_GEOMETRY_TYPES,
} from "../utils/geojsonUtils";
import WidthSelectionModal, { WidthConfig } from "./WidthSelectionModal";

interface FormData {
  name: string;
  value: number;
  assetType: string;
  width?: number;
  /** FK to ConstructionType (null = inherit project default). */
  constructionTypeId: number | null;
  /** Extra per-asset ConstructionSetting overrides applied on top of the type. */
  extraConstructionSettingIds: number[];
}

export interface AssetFormData extends FormData {
  assetId: string | number;
}

interface AssetModalProps {
  mode: "add" | "edit";
  initialData?: Partial<AssetFormData>;
  multiEditIds?: (string | number)[];
  onClose?: () => void;
}

const AddEditAssetModal = ({ mode, initialData, multiEditIds, onClose }: AssetModalProps) => {
  const { projectId } = useParams<{ projectId: string }>();
  const unitSystem = useUnitSystem();
  const [open, setOpen] = useState(false);
  const isMultiEdit = !!multiEditIds && multiEditIds.length > 1;
  const { mutateAsync: addAsset } = useAddAsset(projectId!);
  const { mutateAsync: updateAsset } = useUpdateAsset(
    projectId!,
    initialData?.assetId ?? 0
  );
  const { mutateAsync: updateAssetById } = useUpdateAssetById(projectId!);

  const [formData, setFormData] = useState<FormData>({
    name: "",
    value: 0,
    assetType: "",
    width: undefined,
    constructionTypeId: null,
    extraConstructionSettingIds: [],
  });
  const [errors, setErrors] = useState<
    { name?: string; value?: string; width?: string; file?: string } | {}
  >({});
  const {
    drawingMode,
    drawingFeatureType,
    setDrawingMode,
    setDrawingFeatureType,
    geojson,
    setGeojson,
  } = useContext(DrawingContext);

  // File upload state
  const { assetTypes } = useAssetTypes();
  const { project } = useProject();
  const projectDefaultTypeId = project?.data?.default_construction_type ?? null;
  const constructionTypesQ = useConstructionTypes();
  const constructionSettingsQ = useConstructionSettings();
  const constructionTypes = constructionTypesQ.data?.data ?? [];
  const constructionSettings = constructionSettingsQ.data?.data ?? [];

  const [fileName, setFileName] = useState<string>("");
  const [fileError, setFileError] = useState<string>("");
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filestoreBrowserOpen, setFilestoreBrowserOpen] = useState(false);
  // Width dialog for uploaded line/point features (buffered to polygons on the backend)
  const [widthSelectionOpen, setWidthSelectionOpen] = useState(false);
  const [widthSelectionFile, setWidthSelectionFile] = useState<FilestoreFile | null>(null);
  const [widthFeatureKind, setWidthFeatureKind] = useState<string>("line");
  const [flammableField, setFlammableField] = useState<string>("");
  // Map "flammable" / "not flammable" values to ConstructionType ids during bulk upload.
  const [flammableTrueTypeId, setFlammableTrueTypeId] = useState<number | "">("");
  const [flammableFalseTypeId, setFlammableFalseTypeId] = useState<number | "">("");

  // Default the flammable type pickers to the system "Wood" / "Masonry" types
  // once the construction-type list loads.  Keeps the legacy UX intact.
  useEffect(() => {
    if (constructionTypes.length === 0) return;
    if (flammableTrueTypeId === "") {
      const wood = constructionTypes.find((t) => t.name === "Wood" && t.is_system_default);
      if (wood) setFlammableTrueTypeId(wood.id);
    }
    if (flammableFalseTypeId === "") {
      const masonry = constructionTypes.find(
        (t) => t.name === "Masonry" && t.is_system_default
      );
      if (masonry) setFlammableFalseTypeId(masonry.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [constructionTypes]);

  const isUploadMode = drawingFeatureType === DrawingFeatureType.UPLOAD;
  // Construction (structure type + characteristics) only applies to buildings.
  // For roads/power lines etc. the section is hidden and no type is submitted.
  const isBuildingAsset = isBuildingAssetType(formData.assetType);
  const uploadFields = useMemo(() => collectPropertyFields(geojson), [geojson]);
  const flammableFieldSamples = useMemo(
    () => (flammableField ? collectFieldSamples(geojson, flammableField, 5) : []),
    [geojson, flammableField]
  );

  const settingById = useMemo(() => {
    const m = new Map<number, (typeof constructionSettings)[number]>();
    for (const s of constructionSettings) m.set(s.id, s);
    return m;
  }, [constructionSettings]);

  // Anchor for the "add characteristic" menu.
  const [addCharAnchor, setAddCharAnchor] = useState<null | HTMLElement>(null);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && drawingMode === DrawingMode.ASSET) {
      setOpen(true);
    }
  };

  useEffect(() => {
    if (mode === "add") {
      window.addEventListener("keydown", handleKeyDown);

      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [drawingMode, mode]);

  useEffect(() => {
    if (mode !== "add") return;
    if (drawingFeatureType !== DrawingFeatureType.LINE) return;
    setFormData((prev) => {
      if (typeof prev.width === "number") return prev;
      return { ...prev, width: 40 };
    });
  }, [drawingFeatureType, mode]);

  // Auto-open when Upload feature selected
  useEffect(() => {
    if (
      mode === "add" &&
      drawingMode === DrawingMode.ASSET &&
      drawingFeatureType === DrawingFeatureType.UPLOAD
    ) {
      setOpen(true);
    }
  }, [drawingMode, drawingFeatureType, mode]);

  useEffect(() => {
    if (initialData && mode === "edit") {
      const assetType = initialData.assetType || "";
      const shouldDefaultWidth =
        typeof initialData.width !== "number" && isLineLikeAssetType(assetType);
      setFormData({
        name: initialData.name || "",
        value: initialData.value ?? 0,
        assetType,
        width: shouldDefaultWidth ? 40 : initialData.width,
        constructionTypeId: initialData.constructionTypeId ?? null,
        extraConstructionSettingIds: initialData.extraConstructionSettingIds ?? [],
      });
      setOpen(true);
    }
  }, [initialData, mode]);

  const isLineLikeAssetType = (assetType: string) => {
    return /line|road|highway/i.test(assetType);
  };

  const validate = () => {
    const newErrors: { name?: string; value?: string; width?: string; file?: string } = {};

    if (!isMultiEdit && !formData.name) {
      newErrors.name = "Asset Name is required";
    } else if (formData.value < 0) {
      newErrors.value = "Asset Value should be a positive number";
    }

    const showWidthField =
      drawingFeatureType === DrawingFeatureType.LINE ||
      (mode === "edit" &&
        (isLineLikeAssetType(formData.assetType) ||
          isLineLikeAssetType(initialData?.assetType || "") ||
          typeof initialData?.width === "number"));
    if (showWidthField && typeof formData.width === "number" && formData.width <= 0) {
      newErrors.width = "Asset Width should be a positive number";
    }
    if (drawingFeatureType === DrawingFeatureType.UPLOAD && !fileName) {
      newErrors.file = "Please select a file to upload";
    }

    return newErrors;
  };

  const reset = () => {
    setFormData((prev) => ({
      ...prev,
      name: "",
      value: 0,
      assetType: initialData?.assetType || "",
      width: initialData?.width,
      constructionTypeId: initialData?.constructionTypeId ?? null,
      extraConstructionSettingIds: initialData?.extraConstructionSettingIds ?? [],
    }));
    setFileName("");
    setFileError("");
    setFlammableField("");
    setWidthSelectionOpen(false);
    setWidthSelectionFile(null);
    setDrawingMode(DrawingMode.NONE);
    setDrawingFeatureType(DrawingFeatureType.NONE);
  };

  const handleClose = () => {
    reset();
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const handleChangeValue = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const numericFields = new Set(["value"]);
    const nextValue = numericFields.has(name) ? (value === "" ? 0 : Number(value)) : value;
    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const getWidthDisplayValue = (): string => {
    if (typeof formData.width !== "number") return "";
    if (unitSystem === "imperial") return String(metersToFeet(formData.width));
    return String(formData.width);
  };

  const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const raw = e.target.value;
    if (raw === "") {
      setFormData((prev) => ({ ...prev, width: undefined }));
      return;
    }
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return;
    const meters = unitSystem === "imperial" ? feetToMeters(numeric) : numeric;
    setFormData((prev) => ({ ...prev, width: meters }));
  };

  // ---------------- File handling ----------------
  const handleFilestoreFileSelected = async (filestoreFile: FilestoreFile) => {
    setFileError("");
    setIsFileLoading(true);
    try {
      const geojson = await parseFilestoreFile(filestoreFile.id);
      setGeojson(geojson);
      setFileName(filestoreFile.original_filename);
      setFileError("");

      // Line/point features need a width so the backend can buffer them to polygons
      const hasLines = hasGeometryOfType(geojson, LINE_GEOMETRY_TYPES);
      const hasPoints = hasGeometryOfType(geojson, POINT_GEOMETRY_TYPES);
      if (hasLines || hasPoints) {
        setWidthFeatureKind(
          hasLines && hasPoints ? "line and point" : hasLines ? "line" : "point"
        );
        setWidthSelectionFile(filestoreFile);
        setWidthSelectionOpen(true);
      }
    } catch (err) {
      console.error(err);
      setFileError("Failed to load file from filestore");
    } finally {
      setIsFileLoading(false);
    }
  };

  const handleWidthConfigApply = (config: WidthConfig) => {
    if (geojson) {
      setGeojson(
        applyWidthConfig(geojson, config, [...LINE_GEOMETRY_TYPES, ...POINT_GEOMETRY_TYPES])
      );
    }
  };

  const handleSubmit = async () => {
    const newErrors = validate();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      setIsSubmitting(true);
      try {
        // Construction only applies to buildings — strip any lingering type so a
        // non-building asset can never be saved with one.
        const submitData = isBuildingAsset
          ? formData
          : { ...formData, constructionTypeId: null, extraConstructionSettingIds: [] };
        if (isMultiEdit) {
          await Promise.all(
            multiEditIds!.map((id) => updateAssetById({ assetId: id, formData: submitData }))
          );
        } else if (mode === "edit") {
          await updateAsset(submitData);
        } else {
          await addAsset({
            ...submitData,
            flammableField: isBuildingAsset ? flammableField || null : null,
            flammableTrueTypeId: isBuildingAsset ? flammableTrueTypeId || null : null,
            flammableFalseTypeId: isBuildingAsset ? flammableFalseTypeId || null : null,
          });
        }
        handleClose();
      } catch (error) {
        console.error(`"Error ${mode} asset:`, error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (!open) return null;

  return (
    <>
      <Modal
        open={open}
        onClose={(_event, _reason) => {
          // Don't let a backdrop click or Escape dismiss the modal while a
          // file is parsing or assets are being submitted.
          if (isFileLoading || isSubmitting) return;
          handleClose();
        }}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100%",
          position: "absolute",
        }}
      >
        <Box
          sx={{
            padding: 2,
            borderRadius: 8,
            backgroundColor: "white",
            margin: "auto",
            top: "50%",
            left: "50%",
            width: "30%",
            position: "absolute",
            transform: "translate(-50%, -50%)",
            overflow: "hidden",
            "&:focus": {
              outline: "none",
              boxShadow: "none",
            },
          }}
        >
          {/* Loading Overlay */}
          {(isFileLoading || isSubmitting) && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(255,255,255,0.8)",
                zIndex: 10,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <CircularProgress color="primary" />
              <span>
                {isFileLoading
                  ? "Parsing uploaded file..."
                  : isMultiEdit
                  ? "Saving assets..."
                  : mode === "edit"
                  ? "Saving asset..."
                  : "Importing assets..."}
              </span>
            </Box>
          )}

          <h2>
            {isMultiEdit
              ? `Edit ${multiEditIds!.length} Assets`
              : mode === "edit"
              ? "Edit Asset"
              : "Add New Asset"}
          </h2>
          {drawingFeatureType === DrawingFeatureType.UPLOAD && (
            <Box sx={{ mb: 3 }}>
              <p style={{ fontSize: "0.85em", color: "#666", margin: "0 0 8px" }}>
                Shapefiles must be uploaded as a .zip archive.
              </p>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setFilestoreBrowserOpen(true)}
                >
                  Browse My Files
                </Button>
                {fileName && (
                  <p style={{ fontSize: "0.9em", marginTop: 8 }}>Selected: {fileName}</p>
                )}
              </Box>

              {fileError && (
                <p style={{ color: "red", fontSize: "0.9em", marginTop: 8 }}>{fileError}</p>
              )}

              <FilestoreBrowser
                open={filestoreBrowserOpen}
                onClose={() => setFilestoreBrowserOpen(false)}
                onSelectFile={handleFilestoreFileSelected}
                acceptedFileTypes=".zip,.kml,.geojson,.json"
              />

              {widthSelectionFile && (
                <WidthSelectionModal
                  open={widthSelectionOpen}
                  onClose={() => setWidthSelectionOpen(false)}
                  onApply={handleWidthConfigApply}
                  file={widthSelectionFile}
                  fields={uploadFields}
                  geojson={geojson}
                  featureKindLabel={widthFeatureKind}
                />
              )}

              {widthSelectionFile && !widthSelectionOpen && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {`${widthFeatureKind.charAt(0).toUpperCase()}${widthFeatureKind.slice(1)} features will be buffered to polygons using the configured width.`}
                  </Typography>
                  <Button size="small" onClick={() => setWidthSelectionOpen(true)}>
                    Change widths
                  </Button>
                </Box>
              )}
            </Box>
          )}

          <div style={{ display: "flex", flexDirection: "row", alignItems: "baseline" }}>
            <Icon sx={{ m: 1, color: "#00d4ad" }}>label</Icon>
            <TextField
              label="Asset Name"
              name="name"
              fullWidth
              value={formData.name || ""}
              onChange={handleChangeValue}
              disabled={isMultiEdit}
              placeholder={isMultiEdit ? "Unchanged (multiple assets)" : undefined}
              sx={{ marginBottom: 2 }}
              error={errors && "name" in errors}
              helperText={errors && "name" in errors ? errors.name : ""}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "row", alignItems: "baseline" }}>
            <Icon sx={{ m: 1, color: "#00d4ad" }}>category</Icon>
            <FormControl fullWidth sx={{ mb: 4 }} size="small">
              <InputLabel id="asset-type-label">Asset Type</InputLabel>
              <Select
                labelId="asset-type-label"
                id="asset-type-select"
                value={formData.assetType}
                label="Asset Type"
                onChange={(e) => setFormData((prev) => ({ ...prev, assetType: e.target.value }))}
              >
                {assetTypes?.data?.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <div style={{ display: "flex", flexDirection: "row", alignItems: "baseline" }}>
            <Icon sx={{ m: 1, color: "#00d4ad" }}>attach_money</Icon>
            <TextField
              label="Asset Value"
              name="value"
              InputProps={{
                type: "number",
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              fullWidth
              sx={{ marginBottom: 2 }}
              value={formData.value}
              onChange={handleChangeValue}
              error={errors && "value" in errors}
              helperText={errors && "value" in errors ? errors.value : ""}
            />
          </div>

          {(drawingFeatureType === DrawingFeatureType.LINE ||
            (mode === "edit" &&
              (isLineLikeAssetType(formData.assetType) ||
                isLineLikeAssetType(initialData?.assetType || "") ||
                typeof initialData?.width === "number"))) && (
            <div style={{ display: "flex", flexDirection: "row", alignItems: "baseline" }}>
              <Icon sx={{ m: 1, color: "#00d4ad" }}>straighten</Icon>
              <TextField
                label="Asset Width"
                name="width"
                InputProps={{
                  type: "number",
                  endAdornment: (
                    <InputAdornment position="end">
                      {unitSystem === "imperial" ? "ft" : "m"}
                    </InputAdornment>
                  ),
                }}
                fullWidth
                sx={{ marginBottom: 2 }}
                value={getWidthDisplayValue()}
                onChange={handleWidthChange}
                error={errors && "width" in errors}
                helperText={errors && "width" in errors ? errors.width : ""}
              />
            </div>
          )}

          {/* Construction — buildings only */}
          {isBuildingAsset && (() => {
            // When the asset has no explicit type, fall back to the project
            // default so the user sees the characteristics actually applied.
            const inheritingDefault =
              formData.constructionTypeId == null && projectDefaultTypeId != null;
            const effectiveTypeId =
              formData.constructionTypeId ?? projectDefaultTypeId;
            const selectedType = constructionTypes.find(
              (t) => t.id === effectiveTypeId
            );
            const typeCharIds = new Set<number>(
              (selectedType?.settings ?? []).map((s) => s.id)
            );
            const extraIds = formData.extraConstructionSettingIds.filter(
              (id) => !typeCharIds.has(id)
            );

            type EffChar = {
              id: number;
              name: string;
              category: string;
              inherited: boolean;
            };
            const effective: EffChar[] = [
              ...(selectedType?.settings ?? []).map((s) => ({
                id: s.id,
                name: s.name,
                category: s.category as string,
                inherited: true,
              })),
              ...extraIds
                .map((id) => {
                  const s = settingById.get(id);
                  return s
                    ? {
                        id,
                        name: s.name,
                        category: s.category as string,
                        inherited: false,
                      }
                    : null;
                })
                .filter((x): x is EffChar => x !== null),
            ];

            const available = constructionSettings.filter(
              (s) => !typeCharIds.has(s.id) && !extraIds.includes(s.id)
            );

            const addExtra = (id: number) =>
              setFormData((prev) => ({
                ...prev,
                extraConstructionSettingIds: [
                  ...prev.extraConstructionSettingIds.filter((x) => x !== id),
                  id,
                ],
              }));
            const removeExtra = (id: number) =>
              setFormData((prev) => ({
                ...prev,
                extraConstructionSettingIds:
                  prev.extraConstructionSettingIds.filter((x) => x !== id),
              }));

            return (
              <Box
                sx={{
                  mb: 2,
                  border: "1px solid #e3e6ea",
                  borderRadius: 2,
                  p: 1.5,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <Icon sx={{ color: "#00d4ad" }}>construction</Icon>
                  <Typography sx={{ fontWeight: 600 }}>Construction</Typography>
                </Box>

                <FormControl
                  fullWidth
                  size="small"
                  disabled={!!flammableField}
                  sx={{ mb: 1.5 }}
                >
                  <InputLabel id="construction-type-label">Structure Type</InputLabel>
                  <Select
                    labelId="construction-type-label"
                    value={formData.constructionTypeId ?? ""}
                    label="Structure Type"
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        constructionTypeId:
                          e.target.value === "" ? null : Number(e.target.value),
                      }))
                    }
                  >
                    <MenuItem value="">— Inherit project default —</MenuItem>
                    {constructionTypes.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name}
                        {t.is_system_default ? " (built-in)" : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Characteristics — in multi-edit these apply to every selected asset */}
                {(
                  <Box>
                    {isMultiEdit && (
                      <Typography
                        variant="caption"
                        sx={{ color: "#9aa5b1", display: "block", mb: 1 }}
                      >
                        Characteristics set here replace the extras on all{" "}
                        {multiEditIds!.length} selected assets.
                      </Typography>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "#5f6b7a", fontWeight: 600 }}
                      >
                        Characteristics
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        disabled={available.length === 0}
                        onClick={(e) => setAddCharAnchor(e.currentTarget)}
                        sx={{
                          textTransform: "none",
                          color: "#00a88c",
                          "&:hover": { backgroundColor: "rgba(0,212,173,0.08)" },
                        }}
                      >
                        Add
                      </Button>
                    </Box>

                    {effective.length === 0 ? (
                      <Typography
                        variant="caption"
                        sx={{ color: "#9aa5b1", fontStyle: "italic" }}
                      >
                        {formData.constructionTypeId
                          ? "This type has no characteristics."
                          : "Inherits the project default. Pick a type or add characteristics."}
                      </Typography>
                    ) : (
                      CONSTRUCTION_CATEGORIES.map((cat) => {
                        const items = effective.filter(
                          (c) => c.category === cat.value
                        );
                        if (items.length === 0) return null;
                        return (
                          <Box key={cat.value} sx={{ mb: 1 }}>
                            <Typography
                              sx={{
                                fontSize: "0.62rem",
                                fontWeight: 700,
                                letterSpacing: 0.6,
                                textTransform: "uppercase",
                                color: "#9aa5b1",
                                mb: 0.5,
                              }}
                            >
                              {cat.label}
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {items.map((c) =>
                                c.inherited ? (
                                  <Tooltip
                                    key={`${c.id}-t`}
                                    title={
                                      inheritingDefault
                                        ? `From project default “${selectedType?.name}” — set a type or edit the default to change`
                                        : `From “${selectedType?.name}” — edit the type to change`
                                    }
                                  >
                                    <Chip
                                      label={c.name}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        height: 24,
                                        color: "#5f6b7a",
                                        borderColor: "#d5dae0",
                                        backgroundColor: "#f5f7f9",
                                      }}
                                    />
                                  </Tooltip>
                                ) : (
                                  <Chip
                                    key={`${c.id}-x`}
                                    label={c.name}
                                    size="small"
                                    onDelete={() => removeExtra(c.id)}
                                    sx={{
                                      height: 24,
                                      color: "#00876f",
                                      backgroundColor: "rgba(0,212,173,0.14)",
                                      "& .MuiChip-deleteIcon": {
                                        color: "#00a88c",
                                        "&:hover": { color: "#00876f" },
                                      },
                                    }}
                                  />
                                )
                              )}
                            </Box>
                          </Box>
                        );
                      })
                    )}

                    {/* Add-characteristic menu, grouped by category */}
                    <Menu
                      anchorEl={addCharAnchor}
                      open={Boolean(addCharAnchor)}
                      onClose={() => setAddCharAnchor(null)}
                      slotProps={{ paper: { sx: { maxHeight: 360, width: 280 } } }}
                    >
                      {available.length === 0
                        ? [
                            <MenuItem key="none" disabled>
                              All characteristics added
                            </MenuItem>,
                          ]
                        : CONSTRUCTION_CATEGORIES.flatMap((cat) => {
                            const inCat = available.filter(
                              (s) => s.category === cat.value
                            );
                            if (inCat.length === 0) return [];
                            return [
                              <Typography
                                key={`hdr-${cat.value}`}
                                sx={{
                                  px: 2,
                                  pt: 1,
                                  pb: 0.5,
                                  fontSize: "0.62rem",
                                  fontWeight: 700,
                                  letterSpacing: 0.6,
                                  textTransform: "uppercase",
                                  color: "#9aa5b1",
                                }}
                              >
                                {cat.label}
                              </Typography>,
                              ...inCat.map((s) => (
                                <MenuItem
                                  key={s.id}
                                  onClick={() => addExtra(s.id)}
                                  sx={{ fontSize: "0.9rem" }}
                                >
                                  {s.name}
                                </MenuItem>
                              )),
                            ];
                          })}
                    </Menu>
                  </Box>
                )}
              </Box>
            );
          })()}

          {isBuildingAsset && isUploadMode && uploadFields.length > 0 && (
            <>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textAlign: "center", mb: 1, mt: -1 }}
              >
                — or derive type from data field —
              </Typography>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                }}
              >
                <Icon sx={{ m: 1, mt: 1.2, color: "#00d4ad" }}>local_fire_department</Icon>
                <Box sx={{ flex: 1 }}>
                  <FormControl fullWidth size="small" sx={{ mb: 0.5 }}>
                    <InputLabel id="flammable-field-label">Flammable Field</InputLabel>
                    <Select
                      labelId="flammable-field-label"
                      value={flammableField}
                      label="Flammable Field"
                      disabled={!!formData.constructionTypeId}
                      onChange={(e) => {
                        setFlammableField(e.target.value);
                        if (e.target.value) {
                          setFormData((prev) => ({ ...prev, constructionTypeId: null }));
                        }
                      }}
                    >
                      <MenuItem value="">(None)</MenuItem>
                      {uploadFields.map((f) => (
                        <MenuItem key={f} value={f}>
                          {f}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {flammableField && (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                        {flammableFieldSamples.length > 0
                          ? `Sample values: ${flammableFieldSamples.join(", ")} — non-zero/non-empty rows get the “flammable” type, others get the “non-flammable” type.`
                          : "Non-zero/non-empty rows get the “flammable” type, others get the “non-flammable” type."}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Flammable → type</InputLabel>
                          <Select
                            label="Flammable → type"
                            value={flammableTrueTypeId}
                            onChange={(e) =>
                              setFlammableTrueTypeId(
                                e.target.value === "" ? "" : Number(e.target.value)
                              )
                            }
                          >
                            <MenuItem value="">— None —</MenuItem>
                            {constructionTypes.map((t) => (
                              <MenuItem key={t.id} value={t.id}>
                                {t.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Otherwise → type</InputLabel>
                          <Select
                            label="Otherwise → type"
                            value={flammableFalseTypeId}
                            onChange={(e) =>
                              setFlammableFalseTypeId(
                                e.target.value === "" ? "" : Number(e.target.value)
                              )
                            }
                          >
                            <MenuItem value="">— None —</MenuItem>
                            {constructionTypes.map((t) => (
                              <MenuItem key={t.id} value={t.id}>
                                {t.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </>
                  )}
                </Box>
              </div>
            </>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isFileLoading || isSubmitting}
              sx={{ backgroundColor: "#00d4ad", color: "white" }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default AddEditAssetModal;
